import type { DiscoveriesDto, DiscoveryDto, DiscoveryKind } from '@bk/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import './discoveries.css';

/** Un segno per specie di scoperta, per riconoscerle a colpo d'occhio. */
const ICON: Record<DiscoveryKind, string> = {
  impresa: '🏢',
  sede: '📍',
  addetti: '👥',
  carica: '👤',
  socio: '🤝',
  ateco: '🏷️',
  certificazione: '🏅',
  sito: '🌐',
  pagina: '📄',
  marchio: '🎨',
  registro: '🏛️',
  appalti: '📑',
  libreria: '📚',
};

/** Ogni quanto compare la prossima scoperta arrivata insieme ad altre: una alla volta si leggono. */
const DRIP_MS = 900;
/** Quante se ne vedono prima di «mostra tutte». */
const VISIBLE = 6;

const reducedMotion = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** «1 min 05 s», «42 s». */
const span = (sec: number) => (sec < 60 ? `${sec} s` : `${Math.floor(sec / 60)} min ${String(sec % 60).padStart(2, '0')} s`);
/** Ogni quanto scorre il nastro di ciò che si sta leggendo. */
const ROLL_MS = 2600;

function when(iso: string | null, seen: number): string {
  const d = iso ? new Date(iso) : new Date(seen);
  const today = d.toDateString() === new Date().toDateString();
  return today ? d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
}

/**
 * «Cosa sto trovando» (ADR 0152): mentre si aspetta, le cose vere che il sistema ha già salvato sull'azienda, ognuna con
 * la sua fonte e l'ora, dalla più nuova. Quelle già note quando la pagina si apre ci sono subito; le nuove arrivano una
 * alla volta, con un breve risalto (senza movimento se la persona ha chiesto meno animazioni). Niente testi inventati:
 * se non c'è ancora nulla, lo dice e basta.
 */
export function Discoveries({ tenantId, live = true, className = '' }: { tenantId: string; live?: boolean; className?: string }) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['tenant', tenantId, 'discoveries'],
    queryFn: () => api<DiscoveriesDto>(`/tenants/${tenantId}/onboarding/discoveries`),
    refetchInterval: live ? 3000 : false,
  });
  // Le chiavi mostrate, dalla più nuova, e quando sono comparse qui; quelle arrivate e non ancora mostrate aspettano.
  const [shown, setShown] = useState<string[]>([]);
  const [fresh, setFresh] = useState<Set<string>>(new Set());
  const [all, setAll] = useState(false);
  const seen = useRef(new Map<string, number>());
  const queue = useRef<string[]>([]);
  const first = useRef(true);

  const byKey = new Map((list.data?.scoperte ?? []).map((d) => [d.chiave, d]));

  useEffect(() => {
    const items = list.data?.scoperte;
    if (!items) return;
    const known = new Set([...seen.current.keys(), ...queue.current]);
    const added = items.filter((d) => !known.has(d.chiave));
    const initial = first.current;
    first.current = false;
    if (!added.length) return;
    // Il sito ufficiale appena verificato porta logo e colori: la barra in alto li chiede subito, non al giro dopo.
    if (added.some((d) => d.tipo === 'sito' || d.chiave === 'marchio')) void qc.invalidateQueries({ queryKey: ['tenant', tenantId, 'brand'] });
    if (initial || reducedMotion()) {
      // Ciò che c'era già all'apertura (o con meno animazioni) si vede subito, nell'ordine del server.
      const now = Date.now();
      for (const d of added) seen.current.set(d.chiave, now);
      setShown((s) => [...added.map((d) => d.chiave), ...s]);
      return;
    }
    // Le nuove arrivano dalla più vecchia, così la più nuova finisce in cima.
    queue.current.push(...added.map((d) => d.chiave).reverse());
  }, [list.data, qc, tenantId]);

  useEffect(() => {
    const timer = setInterval(() => {
      const k = queue.current.shift();
      if (!k) return;
      seen.current.set(k, Date.now());
      setShown((s) => [k, ...s]);
      setFresh((f) => new Set(f).add(k));
      setTimeout(() => setFresh((f) => {
        const n = new Set(f);
        n.delete(k);
        return n;
      }), 2600);
    }, DRIP_MS);
    return () => clearInterval(timer);
  }, []);

  // Il tempo che passa, al secondo: il lavoro in corso si vede andare avanti anche tra una lettura e l'altra.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!live) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [live]);
  const work = list.data?.lavoro;
  const since = (s: number) => s + Math.max(0, Math.round((now - list.dataUpdatedAt) / 1000));
  const lastAgo = work?.ultimo_il ? Math.max(0, Math.round((now - Date.parse(work.ultimo_il)) / 1000)) : null;

  const rows = shown.map((k) => byKey.get(k)).filter((d): d is DiscoveryDto => !!d);
  const visible = all ? rows : rows.slice(0, VISIBLE);
  const total = Math.max(list.data?.totale ?? 0, rows.length);

  return (
    <section className={`bk-disc ${className}`} aria-labelledby="bk-disc-title">
      <header className="bk-disc-head">
        {live && <span className="bk-disc-live" aria-hidden />}
        <h3 id="bk-disc-title">Cosa sto trovando sulla tua azienda</h3>
        {rows.length > 0 && <span className="bk-disc-count">{total === 1 ? '1 fatto' : `${total} fatti`}, ognuno con la sua fonte</span>}
      </header>
      {live && work && (work.in_corso.length > 0 || (lastAgo != null && lastAgo < 90)) && (
        <p className="bk-disc-beat" role="status">
          {work.in_corso.length > 0 ? (
            <>
              <span className="bk-disc-beat-on">Al lavoro adesso</span> {work.in_corso[0]!.cosa} · da {span(since(work.in_corso[0]!.da_secondi))}
              {work.in_corso.length > 1 && <span className="bk-disc-beat-more"> e altri {work.in_corso.length - 1} lavori insieme</span>}
            </>
          ) : (
            <>Ultima attività {span(lastAgo!)} fa</>
          )}
        </p>
      )}
      {live && <Ticker reads={work?.letture ?? []} facts={rows} />}
      {rows.length === 0 ? (
        <p className="bk-disc-empty">{list.isError ? 'Non riesco a leggere le scoperte ora: riprovo da solo.' : 'Qui compaiono, man mano, i dati veri che trovo, con la fonte da cui vengono.'}</p>
      ) : (
        <ol className="bk-disc-list" aria-live="polite" aria-relevant="additions">
          {visible.map((d) => (
            <li key={d.chiave} className={fresh.has(d.chiave) ? 'is-new' : undefined}>
              <span className="bk-disc-icon" aria-hidden>
                {ICON[d.tipo] ?? '•'}
              </span>
              <span className="bk-disc-body">
                <span className="bk-disc-text">{d.testo}</span>
                <span className="bk-disc-meta">
                  {d.url ? (
                    <a href={d.url} target="_blank" rel="noreferrer noopener">
                      {d.fonte}
                    </a>
                  ) : (
                    d.fonte
                  )}
                  {' · '}
                  <time dateTime={d.quando ?? undefined}>{when(d.quando, seen.current.get(d.chiave) ?? Date.now())}</time>
                </span>
              </span>
            </li>
          ))}
        </ol>
      )}
      {rows.length > VISIBLE && (
        <button type="button" className="bk-disc-more" onClick={() => setAll((v) => !v)} aria-expanded={all}>
          {all ? 'Mostra solo le ultime' : `Mostra tutte (${rows.length})`}
        </button>
      )}
    </section>
  );
}

/**
 * Il nastro (ADR 0152): una riga che scorre con ciò che il motore sta leggendo davvero (le letture recenti dei suoi
 * strumenti: norme, libreria, documenti, pagine); quando non ne ha, i dati già trovati. Con meno
 * animazioni cambia più piano e senza movimento.
 */
function Ticker({ reads, facts }: { reads: Array<{ quando: string; cosa: string }>; facts: DiscoveryDto[] }) {
  const recent = reads.filter((r) => Date.now() - Date.parse(r.quando) < 120_000).slice(0, 12);
  const pool = recent.length ? recent.map((r) => ({ label: 'Sto leggendo', text: r.cosa })) : facts.slice(0, 20).map((f) => ({ label: 'Già trovato', text: f.testo }));
  const [i, setI] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setI((n) => n + 1), reducedMotion() ? ROLL_MS * 2 : ROLL_MS);
    return () => clearInterval(timer);
  }, []);
  if (!pool.length) return null;
  const item = pool[i % pool.length]!;
  return (
    <div className="bk-disc-ticker" aria-hidden>
      <span className="bk-disc-ticker-label">{item.label}</span>
      <span key={`${i}:${item.text}`} className="bk-disc-ticker-text">
        {item.text}
      </span>
    </div>
  );
}

/** Da quanto è partito un lavoro, al secondo («da 1 min 05 s»): accanto agli spinner, si vede che va avanti. */
export function Elapsed({ since, className = '' }: { since: string | null | undefined; className?: string }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const t = since ? Date.parse(since) : NaN;
  if (Number.isNaN(t)) return null;
  return <span className={`tabular-nums ${className}`}>da {span(Math.max(0, Math.round((now - t) / 1000)))}</span>;
}
